import React, { useCallback } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Reusable components
import ReportWidget from './components/ReportWidget';
import CustomTooltip from './components/CustomTooltip';
import { DataPoint, PercentagePoint } from './components/DataPoint';
import { useTranslation } from 'react-i18next';

/**
 * Summary row displaying Created, Resolved counts and resolution percentage
 */
const SummaryRow = ({
  createdCount,
  resolvedCount,
  resolutionPercentage,
  onCreatedClick,
  onResolvedClick
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-3 gap-md-5 mb-3 py-2">
      <DataPoint
        value={createdCount}
        label={t('shared.created', 'Created')}
        color="#3b82f6"
        onClick={onCreatedClick}
      />

      <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

      <DataPoint
        value={resolvedCount}
        label={t('shared.resolved', 'Resolved')}
        color="#10b981"
        onClick={onResolvedClick}
      />

      <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

      <PercentagePoint
        value={resolutionPercentage.toFixed(1)}
        label={t('reporting.created_vs_resolved.percent_resolved', 'Percent Resolved')}
      />
    </div>
  );
};

/**
 * CreatedVsResolvedWidget - Displays work order creation vs resolution trends
 *
 * Shows:
 * - Summary counts for created and resolved work orders
 * - Resolution percentage
 * - Line chart showing trends over time
 */
const CreatedVsResolvedWidget = ({
  data,
  loading,
  error,
  onCreatedClick,
  onResolvedClick,
  onChartPointClick,
  onDetailsClick
}) => {
  const { t } = useTranslation();
  const { summary, chartData } = data || {};
  const { createdCount = 0, resolvedCount = 0, resolutionPercentage = 0 } = summary || {};
  const isEmpty = !chartData || chartData.length === 0;

  // Handle chart dot click for Created line
  const handleCreatedDotClick = useCallback((data) => {
    if (onChartPointClick && data && data.payload) {
      onChartPointClick(data.payload, 'CREATED');
    }
  }, [onChartPointClick]);

  // Handle chart dot click for Resolved line
  const handleResolvedDotClick = useCallback((data) => {
    if (onChartPointClick && data && data.payload) {
      onChartPointClick(data.payload, 'RESOLVED');
    }
  }, [onChartPointClick]);

  return (
    <ReportWidget
      title={t('reporting.created_vs_resolved.title', 'Created vs. Resolved')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={TrendingUp}
      emptyMessage={t('shared.no_data_available', 'No data available for the selected period.')}
    >
      {/* Summary Data Points */}
      <SummaryRow
        createdCount={createdCount}
        resolvedCount={resolvedCount}
        resolutionPercentage={resolutionPercentage}
        onCreatedClick={onCreatedClick}
        onResolvedClick={onResolvedClick}
      />

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData || []}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="periodLabel"
            stroke="#666"
            tick={{ fontSize: 12 }}
          />
          <YAxis stroke="#666" tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="createdCount"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4, cursor: 'pointer' }}
            activeDot={{ r: 6, cursor: 'pointer', onClick: handleCreatedDotClick }}
            name={t('shared.created', 'Created')}
          />
          <Line
            type="monotone"
            dataKey="resolvedCount"
            stroke="#10b981"
            strokeWidth={3}
            dot={{ fill: '#10b981', strokeWidth: 2, r: 4, cursor: 'pointer' }}
            activeDot={{ r: 6, cursor: 'pointer', onClick: handleResolvedDotClick }}
            name={t('shared.resolved', 'Resolved')}
          />
        </LineChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default CreatedVsResolvedWidget;
